import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon } from '@iconify/react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WhatsAppTemplate } from '@/Domain/api/WhatsAppTemplates/WhatsAppTemplates.api';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ConversationAssigneeEnum } from '@/Domain/enum/ConversationAssignee.enum';
import { useUpdateConversation } from '@/Domain/hooks/Conversation/useUpdateConversation.hook';
import {
  useSendWhatsAppTemplate,
  useWhatsAppTemplates,
} from '@/Domain/hooks/Conversation/useWhatsAppTemplates.hook';
import {
  applyParamValues,
  buildTemplateComponents,
  getBodyText,
  getTemplateText,
  parseParams,
} from '@/Domain/utils/whatsappTemplateParams';

type Props = {
  conversationId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const groupTemplatesByCategory = (templates: WhatsAppTemplate[]) =>
  templates.reduce(
    (acc, template) => {
      const category = template.category || 'OTHER';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(template);
      return acc;
    },
    {} as Record<string, WhatsAppTemplate[]>,
  );

const categoryLabels: Record<string, string> = {
  MARKETING: 'Marketing',
  UTILITY: 'Utility',
  AUTHENTICATION: 'Authentication',
  OTHER: 'Other',
};

type TemplateListViewProps = {
  templates: WhatsAppTemplate[];
  isLoading: boolean;
  t: (key: string) => string;
  onSelect: (template: WhatsAppTemplate) => void;
};

const TemplateListView = ({ templates, isLoading, t, onSelect }: TemplateListViewProps) => {
  const groupedTemplates = groupTemplatesByCategory(templates);
  const categories = Object.keys(groupedTemplates);

  return (
    <div className="w-72">
      <h3
        data-testid="template-dropdown-title"
        className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2"
      >
        <FontAwesomeIcon icon={faWhatsapp} className="text-[#25D366] text-base" />
        {t('whatsapp_template.dropdown_title')}
      </h3>
      <div className="max-h-72 overflow-y-auto -mx-1">
        {isLoading ? (
          <div className="px-2 py-3 text-sm text-foreground-subtle text-center">
            {t('whatsapp_template.loading')}
          </div>
        ) : templates.length === 0 ? (
          <div className="px-2 py-3 text-sm text-foreground-subtle text-center">
            {t('whatsapp_template.no_templates')}
          </div>
        ) : (
          categories.map((category, categoryIndex) => (
            <div key={category}>
              {categoryIndex > 0 && <div className="h-px bg-muted my-1" />}
              <div className="px-2 py-1 text-xs font-medium text-foreground-subtle uppercase">
                {categoryLabels[category] || category}
              </div>
              {groupedTemplates[category].map((template) => {
                const bodyText = getBodyText(template);
                return (
                  <button
                    key={template.id}
                    type="button"
                    data-testid={`template-item-${template.id}`}
                    onClick={() => onSelect(template)}
                    className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-foreground truncate">
                        {template.name}
                      </span>
                      <span className="shrink-0 text-xs text-foreground-subtle bg-muted px-1.5 py-0.5 rounded">
                        {template.language}
                      </span>
                    </div>
                    {bodyText && (
                      <p
                        data-testid={`template-item-body-${template.id}`}
                        className="text-xs text-foreground-subtle mt-0.5 line-clamp-2"
                      >
                        {bodyText}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

type TemplatePreviewViewProps = {
  template: WhatsAppTemplate;
  isPending: boolean;
  clickedButton: 'send' | 'assignAI' | null;
  t: (key: string, options?: Record<string, unknown>) => string;
  onBack: () => void;
  onSend: (template: WhatsAppTemplate, paramValues: Record<string, string>) => void;
  onSendAndAssignToAI: (template: WhatsAppTemplate, paramValues: Record<string, string>) => void;
};

const TemplatePreviewView = ({
  template,
  isPending,
  clickedButton,
  t,
  onBack,
  onSend,
  onSendAndAssignToAI,
}: TemplatePreviewViewProps) => {
  const bodyText = getBodyText(template);
  const params = useMemo(() => (bodyText ? parseParams(bodyText) : []), [bodyText]);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});

  const allParamsFilled = params.every((p) => paramValues[p]?.trim());
  const canSend = params.length === 0 || allParamsFilled;

  const previewText = useMemo(
    () => (bodyText ? applyParamValues(bodyText, params, paramValues) : ''),
    [bodyText, params, paramValues],
  );

  return (
    <div className="w-80" data-testid="template-preview">
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={onBack}
          className="text-foreground-subtle hover:text-muted-foreground transition-colors"
          data-testid="template-preview-back"
        >
          <Icon icon="mingcute:arrow-left-line" className="w-4 h-4" />
        </button>
        <h3 data-testid="template-preview-title" className="text-sm font-semibold text-foreground">
          {t('whatsapp_template.preview.title')}
        </h3>
      </div>

      <div className="mb-3">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-sm font-medium text-foreground">{template.name}</span>
          <span className="text-xs text-foreground-subtle bg-muted px-1.5 py-0.5 rounded">
            {template.language}
          </span>
          <span className="text-xs text-foreground-subtle">
            {categoryLabels[template.category] || template.category}
          </span>
        </div>
      </div>

      <div className="mb-3">
        <label
          data-testid="template-body-label"
          className="text-xs font-medium text-foreground-subtle mb-1 block"
        >
          {t('whatsapp_template.preview.body_label')}
        </label>
        <div
          className="text-sm text-muted-foreground bg-muted rounded-lg p-2.5 whitespace-pre-wrap"
          data-testid="template-preview-body"
        >
          {previewText || t('whatsapp_template.preview.no_body')}
        </div>
      </div>

      {params.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="template-params">
          {params.map((param, index) => (
            <div key={param}>
              <label
                data-testid={`template-param-label-${index}`}
                className="text-xs font-medium text-foreground-subtle mb-1 block"
              >
                {/\{\{\d+\}\}/.test(param)
                  ? t('whatsapp_template.preview.param_label', { number: index + 1 })
                  : param.replace(/\{\{|\}\}/g, '')}
              </label>
              <Input
                size="sm"
                data-testid={`template-param-input-${index}`}
                placeholder={t('whatsapp_template.preview.param_placeholder')}
                value={paramValues[param] || ''}
                onChange={(e) => setParamValues((prev) => ({ ...prev, [param]: e.target.value }))}
              />
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="secondary"
          size="sm"
          data-testid="template-send-button"
          onClick={() => onSend(template, paramValues)}
          disabled={!canSend || isPending}
        >
          {isPending && clickedButton === 'send' ? (
            <Icon icon="mingcute:loading-line" className="animate-spin" />
          ) : null}
          {t('whatsapp_template.preview.send_button')}
        </Button>
        <Button
          variant="dark"
          size="sm"
          data-testid="template-send-assign-ai-button"
          onClick={() => onSendAndAssignToAI(template, paramValues)}
          disabled={!canSend || isPending}
        >
          {isPending && clickedButton === 'assignAI' ? (
            <Icon icon="mingcute:loading-line" className="animate-spin" />
          ) : null}
          {t('whatsapp_template.preview.send_and_assign_ai_button')}
        </Button>
      </div>
    </div>
  );
};

export const WhatsAppTemplateDropdown = ({
  conversationId,
  open: controlledOpen,
  onOpenChange,
}: Props) => {
  const { t } = useTranslation(['inbox_v2']);
  const { templates, isLoading } = useWhatsAppTemplates();
  const { sendTemplateAsync, isPending } = useSendWhatsAppTemplate(conversationId);
  const { handleUpdate } = useUpdateConversation(conversationId);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (value: boolean) => {
    setInternalOpen(value);
    onOpenChange?.(value);
  };
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [clickedButton, setClickedButton] = useState<'send' | 'assignAI' | null>(null);

  const handleSelect = (template: WhatsAppTemplate) => {
    setSelectedTemplate(template);
  };

  const handleBack = () => {
    setSelectedTemplate(null);
  };

  const handleSend = async (
    template: WhatsAppTemplate,
    paramValues: Record<string, string>,
    assignToAI: boolean,
  ) => {
    setClickedButton(assignToAI ? 'assignAI' : 'send');

    const components = buildTemplateComponents(template, paramValues);

    const bodyText = getBodyText(template);
    const params = bodyText ? parseParams(bodyText) : [];
    const resolvedBodyText = applyParamValues(getTemplateText(template), params, paramValues);

    try {
      await sendTemplateAsync({
        template_name: template.name,
        language_code: template.language,
        ...(components.length > 0 ? { components } : {}),
        body_text: resolvedBodyText,
      });
      if (assignToAI) {
        handleUpdate({ assignee: { type: ConversationAssigneeEnum.AI_ASSISTANT } });
      }
      setSelectedTemplate(null);
      setOpen(false);
    } catch {
      // Error toast is shown by the hook's onError handler.
      // Keep the popover open so the user can retry.
    } finally {
      setClickedButton(null);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSelectedTemplate(null);
        }
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild disabled={isPending}>
            <IconButton
              variant="primary_dark_light"
              size="sm"
              type="button"
              disabled={isPending}
              data-testid="template-trigger"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="h-4 w-4" />
            </IconButton>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{t('whatsapp_template.trigger_tooltip')}</TooltipContent>
      </Tooltip>
      <PopoverContent align="center" className="p-3">
        {selectedTemplate ? (
          <TemplatePreviewView
            template={selectedTemplate}
            isPending={isPending}
            clickedButton={clickedButton}
            t={t}
            onBack={handleBack}
            onSend={(template, paramValues) => handleSend(template, paramValues, false)}
            onSendAndAssignToAI={(template, paramValues) => handleSend(template, paramValues, true)}
          />
        ) : (
          <TemplateListView
            templates={templates}
            isLoading={isLoading}
            t={t}
            onSelect={handleSelect}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
